import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { protocolsService, RecognitionProtocolResponse, ProtocolGradeResponse } from "../../../services/protocolsService";
import RecognitionProtocolTable from "./ProtocolTable.tsx";
import toast from "react-hot-toast";
import { BiSave } from "react-icons/bi";
import { normalizeDate } from "../../../common/normalizeDate";
import { formatDateOnlyValue } from "../../../common/dateOnly";

const RecognitionProtocol: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [protocolData, setProtocolData] = useState<RecognitionProtocolResponse | null>(null);
  const [grades, setGrades] = useState<ProtocolGradeResponse[]>([]);
  const [modifiedRows, setModifiedRows] = useState<Set<number>>(new Set());
  const [loadingProtocol, setLoadingProtocol] = useState<boolean>(true);
  const [loadingGrades, setLoadingGrades] = useState<boolean>(true);
  const [errorProtocol, setErrorProtocol] = useState<string | null>(null);
  const [errorGrades, setErrorGrades] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const gradeTypeOptions = useMemo(() => [
    { id: 1, name: "Final" },
    { id: 2, name: "Current" },
    { id: 3, name: "Equivalent grade" },
  ], [t]);

  const fetchProtocol = useCallback(async () => {
    if (!id) {
      setErrorProtocol("Protocol ID is missing.");
      setLoadingProtocol(false);
      return;
    }

    try {
      const protocolData = await protocolsService.getRecognitionProtocol(parseInt(id));
      setProtocolData(protocolData);
      setLoadingProtocol(false);
    } catch (error: any) {
      console.error("Error fetching recognition protocol data:", error);
      setErrorProtocol(
        error.message ||
          "An unexpected error occurred while fetching protocol data."
      );
      setLoadingProtocol(false);
    }
  }, [id]);

  const fetchGrades = useCallback(async () => {
    setLoadingGrades(true);
    if (!id) {
      setErrorGrades("Protocol ID is missing.");
      setLoadingGrades(false);
      return;
    }

    try {
      const gradesData = await protocolsService.getProtocolGrades(id);
      setGrades(gradesData);
      setModifiedRows(new Set());
      setLoadingGrades(false);
    } catch (error: any) {
      console.error("Error fetching recognition grades:", error);
      setErrorGrades(
        error.message ||
          "An unexpected error occurred while fetching grades."
      );
      setLoadingGrades(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProtocol();
    fetchGrades();
  }, [fetchProtocol, fetchGrades]);

  const markRowAsModified = useCallback((gradeId: number) => {
    setModifiedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(gradeId);
      return newSet;
    });
  }, []);

  const handleGradeChange = useCallback((gradeId: number, newGrade: number) => {
    setGrades((prevGrades) =>
      prevGrades.map((grade) =>
        grade.studentGradeId === gradeId ? { ...grade, grade: newGrade } : grade
      )
    );
    markRowAsModified(gradeId);
  }, [markRowAsModified]);

  const handleAttestationChange = useCallback((gradeId: number, newValue: number) => {
    setGrades((prevGrades) =>
      prevGrades.map((grade) =>
        grade.studentGradeId === gradeId
          ? {
              ...grade,
              attestation: newValue === 2 ? false : newValue === 0 ? null : true,
            }
          : grade
      )
    );
    markRowAsModified(gradeId);
  }, [markRowAsModified]);

  const handleGradeTypeChange = useCallback((gradeId: number, newGradeTypeId: number) => {
    const selectedGradeType = gradeTypeOptions.find(
      (option) => option.id === newGradeTypeId
    );
    if (selectedGradeType) {
      setGrades((prevGrades) =>
        prevGrades.map((grade) =>
          grade.studentGradeId === gradeId
            ? {
                ...grade,
                studentGradeTypeId: selectedGradeType.id,
                studentGradeTypeName: selectedGradeType.name,
              }
            : grade
        )
      );
      markRowAsModified(gradeId);
    }
  }, [gradeTypeOptions, markRowAsModified]);

  const handleDateChange = useCallback((gradeId: number, date: Date | null) => {
    if (date) {
      date = normalizeDate(date);
    }

    setGrades((prevGrades) =>
      prevGrades.map((grade) =>
        grade.studentGradeId === gradeId
          ? { ...grade, updateDate: date || undefined }
          : grade
      )
    );
    markRowAsModified(gradeId);
  }, [markRowAsModified]);

  const handleSaveAll = useCallback(async () => {
    if (modifiedRows.size === 0) {
      toast.success(t("No changes to save."));
      return;
    }

    setIsSaving(true);
    try {
      const modifiedGrades = grades.filter((grade) => modifiedRows.has(grade.studentGradeId));

      await Promise.all(
        modifiedGrades.map(async (grade) => {
          try {
            const payload = {
              StudentGradeId: grade.studentGradeId,
              StudentId: grade.studentId,
              ProtocolId: grade.protocolId,
              Grade: grade.grade,
              StudentGradeTypeId: grade.studentGradeTypeId,
              StudentGradeComponents: grade.studentGradeComponents || [],
              Attestation: grade.attestation ?? null,
              ThesisTitle: grade.thesisTitle,
              UpdateDate: grade.updateDate,
            };

            await protocolsService.updateStudentGrade(payload);
          } catch (gradeError: any) {
            const errorMessage = gradeError.message || t("Failed to update grade");
            throw new Error(
              `${t("Error updating grade for student")} ${grade.studentName}: ${errorMessage}`
            );
          }
        })
      );

      await fetchGrades();
      toast.success(t("All changes saved successfully."));
    } catch (error: any) {
      console.error("Error saving recognition grades:", error);
      toast.error(
        error.message ||
          t("An unexpected error occurred while saving the grades.")
      );
    } finally {
      setIsSaving(false);
    }
  }, [fetchGrades, grades, modifiedRows, t]);

  const handleExport = useCallback(async (format: "pdf" | "excel") => {
    if (!id) return;

    try {
      // Placeholder for export API call
      // const blob = await recognitionProtocolsService.downloadRecognitionProtocol(id, format);
      
      // Mock implementation
      toast.success(t(`Export to ${format.toUpperCase()} - Not implemented yet`));
    } catch (error: any) {
      console.error(`Error exporting recognition protocol as ${format}:`, error);
      toast.error(
        error.message ||
          t(`An unexpected error occurred while exporting protocol as ${format}.`)
      );
    }
  }, [id, t]);

  const formatDate = useCallback((dateString?: string) => {
    if (!dateString) return t("N/A");
    return formatDateOnlyValue(dateString);
  }, [t]);

  if (loadingProtocol || loadingGrades) {
    return <p>{t("Loading...")}</p>;
  }

  if (errorProtocol || errorGrades) {
    return (
      <div>
        {errorProtocol && (
          <p className="text-red-500">
            {t("Error")}: {errorProtocol === "Protocol ID is missing." ? t("Protocol ID is missing.") : errorProtocol === "An unexpected error occurred while fetching protocol data." ? t("An unexpected error occurred while fetching protocol data.") : errorProtocol}
          </p>
        )}
        {errorGrades && (
          <p className="text-red-500">
            {t("Error")}: {errorGrades === "Protocol ID is missing." ? t("Protocol ID is missing.") : errorGrades === "An unexpected error occurred while fetching grades." ? t("An unexpected error occurred while fetching grades.") : errorGrades}
          </p>
        )}
      </div>
    );
  }

  if (!protocolData) {
    return <p>{t("No protocol data available.")}</p>;
  }

  return (
    <div className="container mx-auto p-4 bg-white dark:bg-gray-900 dark:text-white">
      {/* Recognition Protocol Header */}
      <div className="bg-gray-100 p-6 rounded-lg shadow-lg mb-6 dark:bg-gray-800">
        <h1 className="text-2xl font-bold text-center dark:text-white">
          {t("TRAKIA UNIVERSITY – STARA ZAGORA")}
        </h1>
        <h2 className="text-xl font-semibold text-center mt-2 dark:text-white">
          {t("Recognition Protocol")} № {protocolData.protocolNumber}
        </h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Student Name")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{protocolData.studentName || t("N/A")}</span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Faculty Number")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{protocolData.facultyNumber || t("N/A")}</span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Speciality")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{protocolData.specialityName}</span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Teacher")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{protocolData.teacherName}</span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Issue Date")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{formatDate(protocolData.issueDate)}</span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Exam Date")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">{formatDate(protocolData.examDate)}</span>
          </div>
          {protocolData.examTypeName && (
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Exam Type")}:
              </span>
              <span className="protocol-info-item-value dark:text-white">{protocolData.examTypeName}</span>
            </div>
          )}
          {protocolData.academicYearName && (
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Academic Year")}:
              </span>
              <span className="protocol-info-item-value dark:text-white">{protocolData.academicYearName}</span>
            </div>
          )}
          {protocolData.currentVersion && (
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Version")}:
              </span>
              <span className="protocol-info-item-value dark:text-white">{protocolData.currentVersion}</span>
            </div>
          )}
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Status")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">
              {protocolData.currentStatus || (protocolData.isSigned ? t("Signed") : t("Draft"))}
            </span>
          </div>
          <div className="protocol-info-item">
            <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
              {t("Signed")}:
            </span>
            <span className="protocol-info-item-value dark:text-white">
              {protocolData.isSigned ? t("Yes") : t("No")}
            </span>
          </div>
          {protocolData.isSigned && protocolData.signedBy && (
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Signed By")}:
              </span>
              <span className="protocol-info-item-value dark:text-white">{protocolData.signedBy}</span>
            </div>
          )}
          {protocolData.isSigned && protocolData.signedOn && (
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Signed On")}:
              </span>
              <span className="protocol-info-item-value dark:text-white">{formatDate(protocolData.signedOn)}</span>
            </div>
          )}
        </div>
        
        {/* Protocol Teachers */}
        {protocolData.protocolTeachers && protocolData.protocolTeachers.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-300 dark:border-gray-600">
            <div className="protocol-info-item">
              <span className="protocol-info-item-label font-semibold mr-2 dark:text-white">
                {t("Protocol Teachers")}:
              </span>
              <div className="protocol-info-item-value dark:text-white">
                {protocolData.protocolTeachers.map((teacher, index) => (
                  <div key={index}>
                    {teacher.teacherFullName} ({teacher.teacherName})
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleSaveAll}
          disabled={isSaving || protocolData.isSigned || modifiedRows.size === 0}
          className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded inline-flex items-center gap-2 ${
            isSaving || protocolData.isSigned || modifiedRows.size === 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          <BiSave className="w-5 h-5" />
          {isSaving ? t("Saving...") : t("Save All Changes")}
        </button>
      </div>

      {/* Recognition Grades Table */}
      <RecognitionProtocolTable
        grades={grades}
        isSigned={protocolData.isSigned}
        modifiedRows={modifiedRows}
        gradeTypeOptions={gradeTypeOptions}
        onGradeChange={handleGradeChange}
        onAttestationChange={handleAttestationChange}
        onGradeTypeChange={handleGradeTypeChange}
        onDateChange={handleDateChange}
      />

      {/* Export Buttons */}
      <div className="flex flex-row flex-wrap justify-center mt-4 gap-4">
        <button
          onClick={() => handleExport("pdf")}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm5 6a1 1 0 10-2 0v3.586L7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
              clipRule="evenodd"
            />
          </svg>
          {t("Export to PDF")}
        </button>
        <button
          onClick={() => handleExport("excel")}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z"
              clipRule="evenodd"
            />
          </svg>
          {t("Export to Excel")}
        </button>
      </div>
    </div>
  );
};

export default RecognitionProtocol; 
